import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBarcode, faFileLines } from '@fortawesome/free-solid-svg-icons'
import type { AssetOrInventory, InventoryDocument } from './types'
import { formatFileSize, formatInventoryStatus, formatJobLabel } from './types'
import PhotoGalleryFrame from './PhotoGalleryFrame'

type TallyItemViewProps = {
  item: AssetOrInventory
  images: string[]
  documents: InventoryDocument[]
  galleryIndex: number
  onGalleryIndexChange: (index: number) => void
  getAssignmentLabel: (item: AssetOrInventory) => string | null
  modalTab?: 'info' | 'history'
  onModalTabChange?: (tab: 'info' | 'history') => void
  historySlot?: React.ReactNode
}

function InfoField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1 w-full min-w-0">
      <span className="text-sm font-medium">{label}</span>
      <div className="text-sm break-words">{children}</div>
    </div>
  )
}

function Section({
  title,
  children,
  className = '',
}: {
  title: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <section className={`rounded-box border border-base-200 bg-base-200/30 p-3 space-y-3 ${className}`}>
      <h4 className="font-semibold text-sm">{title}</h4>
      {children}
    </section>
  )
}

export const TallyItemView: React.FC<TallyItemViewProps> = ({
  item,
  images,
  documents,
  galleryIndex,
  onGalleryIndexChange,
  getAssignmentLabel,
  modalTab = 'info',
  onModalTabChange,
  historySlot,
}) => {
  const isInventory = item.source === 'Inventory'
  const jobLabel = formatJobLabel(item)
  const assignment = getAssignmentLabel(item)

  return (
    <div className="w-full max-w-md mx-auto flex flex-col gap-4">
      {isInventory && onModalTabChange ? (
        <div role="tablist" className="tabs tabs-lift w-full">
          <button
            type="button"
            role="tab"
            className={`tab flex-1 ${modalTab === 'info' ? 'tab-active' : ''}`}
            onClick={() => onModalTabChange('info')}
          >
            Info
          </button>
          <button
            type="button"
            role="tab"
            className={`tab flex-1 ${modalTab === 'history' ? 'tab-active' : ''}`}
            onClick={() => onModalTabChange('history')}
          >
            History
          </button>
        </div>
      ) : null}

      {modalTab === 'history' && isInventory ? (
        historySlot
      ) : (
        <>
          <Section title="Photos">
            <div className="flex flex-col items-center gap-2">
              <PhotoGalleryFrame
                images={images}
                index={galleryIndex}
                onIndexChange={onGalleryIndexChange}
                alt={item.name}
                variant="wide"
                emptyLabel="No photos"
              />
            </div>
          </Section>

          <Section title="Details">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <InfoField label="Type">{item.type || '—'}</InfoField>
              <InfoField label="Status">
                {item.status ? (
                  <span className="badge badge-outline badge-sm">
                    {isInventory ? formatInventoryStatus(item.status) : item.status}
                  </span>
                ) : (
                  '—'
                )}
              </InfoField>
              {isInventory ? (
                <>
                  <InfoField label="Quantity">{item.quantity ?? '—'}</InfoField>
                  <InfoField label="SKU">{item.sku || '—'}</InfoField>
                  <InfoField label="Barcode">
                    {item.barcode ? (
                      <span className="inline-flex items-center gap-1.5 font-mono">
                        <FontAwesomeIcon icon={faBarcode} className="text-base-content/50" />
                        {item.barcode}
                      </span>
                    ) : (
                      <span className="text-warning">None — add in Edit</span>
                    )}
                  </InfoField>
                </>
              ) : (
                <InfoField label="Serial">{item.serialNumber || '—'}</InfoField>
              )}
              <InfoField label="Value">
                {item.value !== undefined && item.value !== null ? `$${item.value}` : '—'}
              </InfoField>
            </div>
          </Section>

          <Section title="Assignment">
            <div className="grid grid-cols-1 gap-3">
              <InfoField label="Assigned to">{assignment || 'Unassigned'}</InfoField>
              <InfoField label="Location">
                {jobLabel || item.location?.address || 'None'}
              </InfoField>
              {!isInventory && item.location?.address && jobLabel && item.location.address !== jobLabel ? (
                <InfoField label="Site">{item.location.address}</InfoField>
              ) : null}
              {item.dateAssigned ? (
                <InfoField label="Date assigned">{item.dateAssigned}</InfoField>
              ) : null}
            </div>
          </Section>

          {!isInventory &&
          (item.externalIdentifiers?.vin ||
            item.externalIdentifiers?.imei ||
            item.externalSource ||
            item.lastSeenAt ||
            item.lastSyncedAt) ? (
            <Section title="Tracking">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {item.externalSource ? (
                  <InfoField label="Source">{item.externalSource}</InfoField>
                ) : null}
                {item.externalIdentifiers?.vin ? (
                  <InfoField label="VIN">
                    <span className="font-mono text-xs">{item.externalIdentifiers.vin}</span>
                  </InfoField>
                ) : null}
                {item.externalIdentifiers?.imei ? (
                  <InfoField label="IMEI">
                    <span className="font-mono text-xs">{item.externalIdentifiers.imei}</span>
                  </InfoField>
                ) : null}
                {item.externalIdentifiers?.deviceUUID ? (
                  <InfoField label="Device UUID">
                    <span className="font-mono text-xs break-all">
                      {item.externalIdentifiers.deviceUUID}
                    </span>
                  </InfoField>
                ) : null}
                {item.externalIdentifiers?.trackerId ? (
                  <InfoField label="Tracker ID">{item.externalIdentifiers.trackerId}</InfoField>
                ) : null}
                {item.lastSeenAt ? (
                  <InfoField label="Last seen">
                    {new Date(item.lastSeenAt).toLocaleString()}
                  </InfoField>
                ) : null}
                {item.lastSyncedAt ? (
                  <InfoField label="Last synced">
                    {new Date(item.lastSyncedAt).toLocaleString()}
                  </InfoField>
                ) : null}
              </div>
            </Section>
          ) : null}

          <Section title="Notes">
            <p className="text-sm whitespace-pre-wrap">
              {item.notes?.trim() ? item.notes : <span className="text-base-content/55">No notes</span>}
            </p>
          </Section>

          {isInventory ? (
            <Section title="Documents">
              {documents.length > 0 ? (
                <ul className="space-y-2">
                  {documents.map((doc, idx) => (
                    <li key={`${doc.fileName}-${idx}`}>
                      <a
                        href={doc.dataUrl}
                        download={doc.fileName}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-2 rounded-box border border-base-300 bg-base-100 px-3 py-2 text-sm hover:bg-base-200"
                      >
                        <FontAwesomeIcon icon={faFileLines} className="text-base-content/50 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-medium link link-hover">{doc.fileName}</div>
                          <div className="text-xs text-base-content/55">{formatFileSize(doc.size)}</div>
                        </div>
                      </a>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-base-content/55">No documents attached</p>
              )}
            </Section>
          ) : null}
        </>
      )}
    </div>
  )
}

export default TallyItemView
